import { FC, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { MessageSquareText, X } from 'lucide-react';
import { useAppDispatch, useAppSelector } from '@store/hooks';
import { useGetDriverChatUnreadCountQuery } from '@store/rtk/driverChat';
import {
  closeDriverChatDrawer,
  closeDriverChatPanel,
  latchDriverChatLauncher,
  openPanelConversation,
  resetDriverChat,
  selectDriverChat,
  toggleDriverChatPanel,
} from '@store/slices/driverChat/driverChatSlice';
import { useDriverChatSocket } from '@component/DriverChat/hooks/useDriverChatSocket';
import { useDriverChatAccess } from '@component/DriverChat/hooks/useDriverChatAccess';
import { useDriverConversations } from '@component/DriverChat/hooks/useDriverConversations';
import { useStartDriverChat } from '@component/DriverChat/hooks/useStartDriverChat';
import { useStartableSegments } from '@component/DriverChat/hooks/useStartableSegments';
import { ConversationList } from '@component/DriverChat/ConversationList/ConversationList';
import { ChatThread } from '@component/DriverChat/ChatThread/ChatThread';
import { DriverChatDrawer } from '@component/DriverChat/DriverChatDrawer/DriverChatDrawer';

const PANEL_TITLE_ID = 'dc-panel-title';


/**
 * Dispatcher-only entry point: the floating button, its conversations panel,
 * and the ride-scoped drawer opened from "Message driver".
 */
export const DriverChatLauncher: FC = () => {
  const dispatch = useAppDispatch();
  const enabled = useDriverChatAccess();

  const { isPanelOpen, panelConversationId, drawerSeed, isLauncherLatched } =
    useAppSelector(selectDriverChat);
  // ACTIVE and TODAY are always loaded: any existing conversation keeps the launcher visible.
  const { conversations, isLoading, isError, refetch } = useDriverConversations(
    !enabled,
    true
  );
  const startable = useStartableSegments(enabled, conversations);
  const { start, isStarting } = useStartDriverChat();
  const { data: unreadTotal = 0 } = useGetDriverChatUnreadCountQuery(
    undefined,
    {
      skip: !enabled,
    }
  );

  useDriverChatSocket(enabled);

  // A driver has accepted a ride: from that moment the launcher shows on every
  // screen (until reload), even before the first message is sent.
  const isReadyToChat = startable.length > 0;
  useEffect(() => {
    if (enabled && isReadyToChat) dispatch(latchDriverChatLauncher());
  }, [enabled, isReadyToChat]);

  useEffect(
    () => () => {
      dispatch(resetDriverChat());
    },
    []
  );

  useEffect(() => {
    if (!isPanelOpen || drawerSeed) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dispatch(closeDriverChatPanel());
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isPanelOpen, drawerSeed]);

  if (!enabled) return null;

  // Visible on any screen once a conversation exists (today's closed ones too) or
  // once a driver has accepted a ride (latched). Otherwise there is nothing to show.
  const hasConversation = conversations.length > 0;
  if (!hasConversation && !isLauncherLatched && !isPanelOpen && !drawerSeed) {
    return null;
  }

  const panelConversation =
    panelConversationId != null
      ? conversations.find((c) => c.conversationId === panelConversationId)
      : undefined;

  return createPortal(
    <div className="dc-scope">
      {isPanelOpen && (
        <div
          className="dc-panel"
          role="dialog"
          aria-labelledby={PANEL_TITLE_ID}
          data-test-id="driver-chat-panel"
        >
          {panelConversation ? (
            <ChatThread
              key={panelConversation.conversationId}
              conversationId={panelConversation.conversationId}
              seed={panelConversation}
              mode="panel"
              titleId={PANEL_TITLE_ID}
              onBack={() => dispatch(openPanelConversation(null))}
              onClose={() => dispatch(closeDriverChatPanel())}
              dataTestId="driver-chat-panel"
            />
          ) : (
            <ConversationList
              conversations={conversations}
              isLoading={isLoading}
              isError={isError}
              onReload={refetch}
              startable={startable}
              onStart={start}
              isStarting={isStarting}
              activeConversationId={panelConversationId}
              onOpen={(conversationId) =>
                dispatch(openPanelConversation(conversationId))
              }
              onClose={() => dispatch(closeDriverChatPanel())}
              titleId={PANEL_TITLE_ID}
            />
          )}
        </div>
      )}

      <button
        type="button"
        className={`dc-fab ${isPanelOpen ? 'is-open' : ''}`}
        aria-label={
          isPanelOpen ? 'Close driver messages' : 'Open driver messages'
        }
        aria-expanded={isPanelOpen}
        onClick={() => dispatch(toggleDriverChatPanel())}
        data-test-id="driver-chat-launcher"
      >
        {isPanelOpen ? (
          <X size={22} strokeWidth={2} aria-hidden="true" />
        ) : (
          <MessageSquareText size={24} strokeWidth={1.9} aria-hidden="true" />
        )}
        {!isPanelOpen && unreadTotal > 0 && (
          <span className="dc-badge dc-fab__badge">
            {unreadTotal > 99 ? '99+' : unreadTotal}
          </span>
        )}
      </button>

      {drawerSeed && (
        <DriverChatDrawer
          seed={drawerSeed}
          onClose={() => dispatch(closeDriverChatDrawer())}
        />
      )}
    </div>,
    document.body
  );
};
